/**
 * PermissionGate Component
 * Renderiza conteúdo condicionalmente baseado nas permissões do utilizador
 */

import React, { ReactNode } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { BUSINESS_ROLES, BusinessRole } from '../types/portal.types';

interface PermissionGateProps {
  /** Permissão ou array de permissões requeridas (OR) */
  permission?: string | string[];
  /** Roles permitidos (OR) */
  roles?: string[];
  /** Requer que seja um utilizador de negócio */
  requireBusinessUser?: boolean;
  /** Conteúdo a renderizar se autorizado */
  children: ReactNode;
  /** Conteúdo alternativo se não autorizado */
  fallback?: ReactNode;
}

/**
 * Componente que renderiza conteúdo apenas se o utilizador tiver as permissões necessárias
 */
export const PermissionGate: React.FC<PermissionGateProps> = ({
  permission,
  roles,
  requireBusinessUser,
  children,
  fallback = null,
}) => {
  const { user, permissions } = useAuth();

  // Se não há utilizador, não renderiza
  if (!user) {
    return <>{fallback}</>;
  }

  // Admin tem acesso total
  if (user.role === 'PFADM') {
    return <>{children}</>;
  }

  // Verificar se é utilizador de negócio
  if (requireBusinessUser) {
    if (!BUSINESS_ROLES.includes(user.role as BusinessRole)) {
      return <>{fallback}</>;
    }
  }

  // Verificar roles
  if (roles && roles.length > 0) {
    if (!roles.includes(user.role)) {
      return <>{fallback}</>;
    }
  }

  // Verificar permissões
  if (permission) {
    const requiredPermissions = Array.isArray(permission) ? permission : [permission];
    const hasPermission = requiredPermissions.some((p) => permissions.includes(p));

    if (!hasPermission) {
      return <>{fallback}</>;
    }
  }

  return <>{children}</>;
};

/**
 * Hook para verificar permissões programaticamente
 */
export const usePermissions = () => {
  const { user, permissions } = useAuth();

  const hasPermission = (permission: string | string[]): boolean => {
    if (!user) return false;

    // Admin tem acesso total
    if (user.role === 'PFADM') return true;

    const required = Array.isArray(permission) ? permission : [permission];
    return required.some((p) => permissions.includes(p));
  };

  const hasRole = (roles: string | string[]): boolean => {
    if (!user) return false;

    const allowed = Array.isArray(roles) ? roles : [roles];
    return allowed.includes(user.role);
  };

  const isBusinessUser = (): boolean => {
    if (!user) return false;
    return BUSINESS_ROLES.includes(user.role as BusinessRole);
  };

  const isAdmin = (): boolean => {
    return user?.role === 'PFADM';
  };

  return {
    permissions,
    hasPermission,
    hasRole,
    isBusinessUser,
    isAdmin,
  };
};

export default PermissionGate;
